import { MyLearningItem, MyLearningData, RecentlyViewedLessonItem } from 'src/services/http/myLearning';
import { fetchGraphQL } from 'src/services/contentful/client';
import { GetAllMyLearningEntriesQuery, GetAllMyLearningEntriesQueryVariables } from 'src/graphql/generated/graphql';
import {
  CollectedDataItem,
  CourseEntry,
  GET_ALL_MY_LEARNING_ENTRIES,
  LearningPathEntry,
  LessonEntry,
  MatchingDataItem,
} from 'src/graphql/queries/GetAllMyLearningEntries';
import { getContentProduct } from 'src/utils/getContentProduct';
import { getSlug } from 'src/utils/slug';
import { getProgressForLessons, LearningProgressStatus } from 'src/services/http/progress';

import {
  MyLearningLoaderProps,
  MyLearningResourceData,
  MyLearningResource,
  RecentlyViewedLessonResource,
  ArchivedContentResource,
} from '../types';

interface SubLessonProgressMap {
  [lessonId: string]: LearningProgressStatus;
}

export const getCoursesAndLearningPathsTitleAndSlugs = async (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  myLearningData: MyLearningData | null,
  archivedContentData: ArchivedContentResource[] | null,
  savedContentfulIds?: Set<string>,
): Promise<MyLearningLoaderProps> => {
  if (!recentlyViewedLessonsData && !myLearningData && !archivedContentData) {
    return {
      recentlyViewedLessonsResources: null,
      myLearningResourceData: null,
      archivedContentResources: null,
    };
  }

  const contentfulIds = getAllContentfulIds(recentlyViewedLessonsData, myLearningData, savedContentfulIds);

  const response = await fetchGraphQL<GetAllMyLearningEntriesQuery, GetAllMyLearningEntriesQueryVariables>(
    GET_ALL_MY_LEARNING_ENTRIES,
    { contentfulIds },
  );

  if (
    !response.smCourseCollection.items.length &&
    !response.learningPathCollection.items.length &&
    !response.smLessonCollection.items.length
  ) {
    return {
      recentlyViewedLessonsResources: null,
      myLearningResourceData: null,
      archivedContentResources: archivedContentData,
    };
  }

  const collectedData: CollectedDataItem[] = [
    ...response.smCourseCollection.items,
    ...response.learningPathCollection.items,
    ...response.smLessonCollection.items,
  ];

  const learningPathLessonIds =
    response.learningPathCollection?.items?.flatMap(
      (item) =>
        item.coursesCollection?.items?.flatMap(
          (course) => course.lessonsCollection?.items?.map((lesson) => lesson.sys.id),
        ),
    ) ?? [];
  const courseLessonIds =
    response.smCourseCollection?.items?.flatMap(
      (item) => item.lessonsCollection?.items?.map((lesson) => lesson.sys.id),
    ) ?? [];

  const subLessonProgressData = await getProgressForLessons([...learningPathLessonIds, ...courseLessonIds]);
  const subLessonProgressMap: SubLessonProgressMap =
    subLessonProgressData?.lessons.reduce((acc, lesson) => {
      acc[lesson.lessonId] = lesson.status;

      return acc;
    }, {} as SubLessonProgressMap) ?? {};

  return {
    recentlyViewedLessonsResources: transformRecentlyViewedLessonsData(recentlyViewedLessonsData, collectedData),
    myLearningResourceData: transformMyLearningData(
      myLearningData,
      collectedData,
      subLessonProgressMap,
      savedContentfulIds,
    ),
    archivedContentResources: archivedContentData,
  };
};

const getAllContentfulIds = (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  myLearningData: MyLearningData | null,
  savedContentfulIds?: Set<string>,
): string[] => {
  const contentfulIds =
    recentlyViewedLessonsData
      ?.filter(
        (item: RecentlyViewedLessonItem) => !item.subCollectionTotal || item.progressTotal < item.subCollectionTotal,
      )
      .map((item) => item.contentfulId) ?? [];

  const courseContentfulIds =
    recentlyViewedLessonsData
      ?.filter((item) => item.courseContentfulId)
      .map((item) => item.courseContentfulId as string) ?? [];

  const learningPathContentfulIds =
    recentlyViewedLessonsData
      ?.filter((item) => item.learningPathContentfulId)
      .map((item) => item.learningPathContentfulId as string) ?? [];

  const myLearningContentfulIds =
    (myLearningData && [
      ...myLearningData.courses.map((item) => item.contentfulId),
      ...myLearningData.learningPath.map((item) => item.contentfulId),
    ]) ??
    [];

  const savedIds = savedContentfulIds ? Array.from(savedContentfulIds) : [];

  return [
    ...contentfulIds,
    ...courseContentfulIds,
    ...learningPathContentfulIds,
    ...myLearningContentfulIds,
    ...savedIds,
  ];
};

const transformRecentlyViewedLessonsData = (
  recentlyViewedLessonsData: RecentlyViewedLessonItem[] | null,
  collectedData: CollectedDataItem[],
): RecentlyViewedLessonResource[] | null => {
  if (!recentlyViewedLessonsData) {
    return null;
  }

  return recentlyViewedLessonsData
    .map((item) => mapToRecentlyViewedLessonResource(item, collectedData))
    .filter((item): item is RecentlyViewedLessonResource => !!item);
};

const getLessonStatus = (lesson: RecentlyViewedLessonItem): LearningProgressStatus => {
  if (lesson.progressTotal === 0) {
    return LearningProgressStatus.NOT_STARTED;
  }
  if (lesson.progressTotal === lesson.subCollectionTotal) {
    return LearningProgressStatus.COMPLETED;
  }

  return LearningProgressStatus.IN_PROGRESS;
};

const mapToRecentlyViewedLessonResource = (
  item: RecentlyViewedLessonItem,
  collectedItems: CollectedDataItem[],
): RecentlyViewedLessonResource | null => {
  const matchingItem = collectedItems.find((value) => value.sys.id === item.contentfulId);
  const matchingCourseItem = collectedItems.find((value) => value.sys.id === item.courseContentfulId);
  const matchingLearningPathItem = collectedItems.find((value) => value.sys.id === item.learningPathContentfulId);

  if (matchingItem) {
    const lessonProducts = (matchingItem as LessonEntry).products;

    return {
      ...item,
      title: matchingItem.title,
      slug: getSlug(matchingItem),
      lastVisitedDate: new Date(item.lastVisitedDate),
      duration: (matchingItem as LessonEntry).duration,
      product: getContentProduct(lessonProducts, true),
      productsList: lessonProducts || [],
      status: getLessonStatus(item),
      subCollectionTotal: item.subCollectionTotal || (matchingItem as LessonEntry).sectionListCollection.total,
      ...(matchingCourseItem ? { courseSlug: getSlug(matchingCourseItem) } : {}),
      ...(matchingLearningPathItem ? { learningPathSlug: getSlug(matchingLearningPathItem) } : {}),
    };
  }

  return null;
};

export const transformMyLearningData = (
  myLearningData: MyLearningData | null,
  collectedItems: CollectedDataItem[],
  subLessonProgressMap: SubLessonProgressMap,
  savedContentfulIds?: Set<string>,
): MyLearningResourceData => {
  const learningPaths =
    myLearningData?.learningPath
      .map((item) => {
        const resource = mapToMyLearningResources(item, collectedItems, subLessonProgressMap);

        if (resource && savedContentfulIds) {
          resource.isSaved = savedContentfulIds.has(resource.contentfulId);
        }

        return resource;
      })
      .filter((item): item is MyLearningResource => !!item) ?? [];

  const courses =
    myLearningData?.courses
      .map((item) => {
        const resource = mapToMyLearningResources(item, collectedItems, subLessonProgressMap);

        if (resource && savedContentfulIds) {
          resource.isSaved = savedContentfulIds.has(resource.contentfulId);
        }

        return resource;
      })
      .filter((item): item is MyLearningResource => !!item) ?? [];

  return {
    learningPaths: learningPaths.length ? learningPaths : null,
    courses: courses.length ? courses : null,
  };
};

const getStatusForCourse = (
  course: CourseEntry,
  subLessonProgressMap: SubLessonProgressMap,
): LearningProgressStatus => {
  const lessons = course.lessonsCollection.items ?? [];

  // Check if any lesson is in progress
  const hasInProgressLesson = lessons.some(
    (lesson) => subLessonProgressMap[lesson.sys.id] === LearningProgressStatus.IN_PROGRESS,
  );

  if (hasInProgressLesson) {
    return LearningProgressStatus.IN_PROGRESS;
  }

  // Check if all lessons are completed
  const allLessonsCompleted = lessons.every(
    (lesson) => subLessonProgressMap[lesson.sys.id] === LearningProgressStatus.COMPLETED,
  );

  if (allLessonsCompleted) {
    return LearningProgressStatus.COMPLETED;
  }

  // If no lessons are started, it's not started
  return LearningProgressStatus.NOT_STARTED;
};

const mapToMyLearningResources = (
  item: MyLearningItem,
  collectedItems: CollectedDataItem[],
  subLessonProgressMap: SubLessonProgressMap,
): MyLearningResource | null => {
  const matchingItem = collectedItems.find((data) => data.sys.id === item.contentfulId) as MatchingDataItem | undefined;
  const matchingLearningPathItem = collectedItems.find((value) => value.sys.id === item.learningPathContentfulId);

  // append courses if learning path
  const learningPathProperties = (matchingItem as LearningPathEntry)?.coursesCollection
    ? {
        coursesCollection: {
          items: (matchingItem as LearningPathEntry).coursesCollection.items.map((course) => ({
            ...course,
            status: getStatusForCourse(course as CourseEntry, subLessonProgressMap),
          })),
        },
      }
    : {};

  // append lessons if course
  const courseProperties = (matchingItem as CourseEntry)?.lessonsCollection
    ? {
        lessonsCollection: {
          items: (matchingItem as CourseEntry).lessonsCollection.items.map((lesson) => ({
            ...lesson,
            status: subLessonProgressMap[lesson.sys.id],
          })),
        },
        status: getStatusForCourse(matchingItem as CourseEntry, subLessonProgressMap),
      }
    : {};

  if (matchingItem) {
    return {
      ...item,
      product: getContentProduct(matchingItem.product, true),
      productsList: matchingItem.product || [],
      title: matchingItem.title,
      slug: getSlug(matchingItem),
      badgeUrl: (matchingItem as CourseEntry).reward?.badgeCdnUrl,
      ...learningPathProperties,
      ...courseProperties,
      ...(matchingLearningPathItem ? { learningPathSlug: getSlug(matchingLearningPathItem) } : {}),
    };
  }

  return null;
};
